

import { Injectable } from '@angular/core';
import { EventFilterType } from '../../event/models/event-filter-type-enum';
import { EventSearchFilter } from '../../event/models/event-filter';
import { OrderedSet } from 'immutable';
import { EventMenuSearchFilter } from '../../event/models/event-menu-search-filter';
import { EventFilterUtilsService } from '../../event/service/event-filter-util-service';
import { DateUtil } from '../../shared/utils/date.util';
import { PaymentType } from '../../event/models/payment-type';

@Injectable()
export class EventMenuSearchFilterMapper {


  toSearchFilterSet(filterRecord: EventMenuSearchFilter, isMultiVendor: boolean): OrderedSet<EventSearchFilter> {
    let filterSet: OrderedSet<EventSearchFilter> = OrderedSet([]);

    if (filterRecord.commissionId && filterRecord.commissionId !== '') {
      filterSet = filterSet.add( this.asSearchFilter(EventFilterType.COMMISSION_ID, filterRecord.commissionId) );
    }

    if (filterRecord.fundingReferenceId && filterRecord.fundingReferenceId !== '') {
      filterSet = filterSet.add( this.asSearchFilter(EventFilterType.FUNDING_REFERENCE_ID,
        filterRecord.fundingReferenceId) );
    }

    if (filterRecord.paymentReferenceId && filterRecord.paymentReferenceId !== '') {
      filterSet = filterSet.add( this.asSearchFilter(EventFilterType.PAYMENT_REFERENCE_ID,
        filterRecord.paymentReferenceId));
    }

    // Add prepayment type filter if hasPrepayment is checked
    if (filterRecord.hasPrepayment) {
      filterSet = filterSet.add( this.asSearchFilter(EventFilterType.PAYMENT_TYPE, PaymentType.PREPAYMENT));
    }

    if (isMultiVendor) {
      if ((filterRecord.endDate && filterRecord.endDate !== '') && (filterRecord.startDate && filterRecord.startDate !== '')) {
        filterSet = filterSet.add( this.asSearchFilter(EventFilterType.DATE_RANGE_AGENCY,
          EventFilterUtilsService.getDateRangeFilterValue(filterRecord.startDate, filterRecord.endDate)));
      } else if (filterRecord.startDate && filterRecord.startDate !== '') {
        filterSet = filterSet.add( this.asSearchFilter(EventFilterType.DATE_AFTER_AGENCY,
          DateUtil.formatDate(filterRecord.startDate)));
      } else if (filterRecord.endDate && filterRecord.endDate !== '') {
        filterSet = filterSet.add( this.asSearchFilter(EventFilterType.DATE_BEFORE_AGENCY,
          DateUtil.formatDate(filterRecord.endDate)));
      }
    } else {
      if ((filterRecord.endDate && filterRecord.endDate !== '') && (filterRecord.startDate && filterRecord.startDate !== '')) {
        filterSet = filterSet.add( this.asSearchFilter(EventFilterType.DATE_RANGE_PROPERTY,
          EventFilterUtilsService.getDateRangeFilterValue(filterRecord.startDate, filterRecord.endDate)));
      } else if (filterRecord.startDate && filterRecord.startDate !== '') {
        filterSet = filterSet.add( this.asSearchFilter(EventFilterType.DATE_AFTER_PROPERTY,
          DateUtil.formatDate(filterRecord.startDate)));
      } else if (filterRecord.endDate && filterRecord.endDate !== '') {
        filterSet = filterSet.add( this.asSearchFilter(EventFilterType.DATE_BEFORE_PROPERTY,
          DateUtil.formatDate(filterRecord.endDate)));
      }
    }


    if (filterRecord.eventStatusCompleted) {
      filterSet = filterSet.add( this.asSearchFilter(EventFilterType.COMPLETED,
        JSON.stringify(filterRecord.eventStatusCompleted)));
    }

    if (filterRecord.eventStatusMissingAgency) {
      filterSet = filterSet.add( this.asSearchFilter(EventFilterType.MISSING_AGENCY,
        JSON.stringify(filterRecord.eventStatusMissingAgency)));
    }

    if (filterRecord.includeArchivedEvents) {
      filterSet = filterSet.add( this.asSearchFilter(EventFilterType.ARCHIVED,
        JSON.stringify(filterRecord.includeArchivedEvents)));
    }

    if (filterRecord.includeCommRetentionEvents) {
      filterSet = filterSet.add( this.asSearchFilter(EventFilterType.COMM_RETENTION,
        JSON.stringify(filterRecord.includeCommRetentionEvents)));
    }

    if (filterRecord.includeDeletedEvents) {
      filterSet = filterSet.add( this.asSearchFilter(EventFilterType.DELETED,
        JSON.stringify(filterRecord.includeDeletedEvents)));
    }

    if (filterRecord.guestName) {
      filterSet = filterSet.add( this.asSearchFilter(EventFilterType.GUEST_NAME, filterRecord.guestName));
    }

    filterSet = this.buildPaymentFilters(filterRecord, filterSet);

    if (filterRecord.dashboardPaymentStatus && filterRecord.dashboardPaymentStatus !== '') {
      filterSet = this.buildDashboardPaymentFilter(filterRecord, filterSet);
    }

    if (filterRecord.dashboardMissingContactEmail) {
      filterSet = this.buildDashboardMissingEmailContact(filterRecord, filterSet);
    }


    // Event payment filters
    filterSet = this.buildEventPaymentFilters(filterRecord, filterSet);

    return filterSet;
  }

  private asSearchFilter(filterField: EventFilterType, filterValue: string): EventSearchFilter {
    const filter = new EventSearchFilter({
      filterField: filterField.toString(),
      filterValue: filterValue ? filterValue.trim() : filterValue
    });

    return filter;
  }

  private buildPaymentFilters(filterRecord: EventMenuSearchFilter,
                              filterSet: OrderedSet<EventSearchFilter>): OrderedSet<EventSearchFilter> {

    const paymentStatusRecord = filterRecord.paymentStatus;

    // reapply selected filters
    if (paymentStatusRecord.paid) {
      filterSet = filterSet.add( this.asSearchFilter(EventFilterType.PAYMENT_STATUS, 'paid'));
    }

    if (paymentStatusRecord.inProcess) {
      filterSet = filterSet.add( this.asSearchFilter(EventFilterType.PAYMENT_STATUS, 'paymentInProcess'));
    }

    if (paymentStatusRecord.unpaid) {
      filterSet = filterSet.add( this.asSearchFilter(EventFilterType.PAYMENT_STATUS, 'unpaid'));
    }

    if (paymentStatusRecord.cancelled) {
      filterSet = filterSet.add( this.asSearchFilter(EventFilterType.PAYMENT_STATUS, 'cancelled'));
    }

    return filterSet;
  }

  private buildDashboardPaymentFilter(filterRecord: EventMenuSearchFilter,
    filterSet: OrderedSet<EventSearchFilter>): OrderedSet<EventSearchFilter> {

      const paymentStatusName = filterRecord.dashboardPaymentStatus;

      if (paymentStatusName && paymentStatusName !== '') {
        filterSet = filterSet.add( this.asSearchFilter(EventFilterType.PAYMENT_STATUS, paymentStatusName));
      }

      return filterSet;
  }

  private buildDashboardMissingEmailContact(filterRecord: EventMenuSearchFilter,
    filterSet: OrderedSet<EventSearchFilter>): OrderedSet<EventSearchFilter> {

      const missingContactEmail = filterRecord.dashboardMissingContactEmail;

      if (missingContactEmail && missingContactEmail !== '') {
        filterSet = filterSet.add( this.asSearchFilter(EventFilterType.MISSING_EMAIL_CONTACT, missingContactEmail));
      }

      return filterSet;
  }

  private buildEventPaymentFilters(filterRecord: EventMenuSearchFilter,
                              filterSet: OrderedSet<EventSearchFilter>): OrderedSet<EventSearchFilter> {

    const eventPaymentStatusRecord = filterRecord.eventPaymentStatus;

    // reapply selected filters
    if (eventPaymentStatusRecord.paid) {
      filterSet = filterSet.add( this.asSearchFilter(EventFilterType.EVENT_PAYMENT_STATUS, 'paid'));
    }

    if (eventPaymentStatusRecord.partial) {
      filterSet = filterSet.add( this.asSearchFilter(EventFilterType.EVENT_PAYMENT_STATUS, 'partial'));
    }

    if (eventPaymentStatusRecord.unpaid) {
      filterSet = filterSet.add( this.asSearchFilter(EventFilterType.EVENT_PAYMENT_STATUS, 'unpaid'));
    }

    return filterSet;
  }

  toEventMenuSearchFilter(filterSet: OrderedSet<EventSearchFilter>, isMultiVendor: boolean): EventMenuSearchFilter {
    let filterRecord: any = {};

    filterRecord.eventStatusMissingAgency = this.hasSearchFilter(filterSet, EventFilterType.MISSING_AGENCY);
    filterRecord.eventStatusCompleted = this.hasSearchFilter(filterSet, EventFilterType.COMPLETED);
    filterRecord.includeArchivedEvents = this.hasSearchFilter(filterSet, EventFilterType.ARCHIVED);
    filterRecord.includeCommRetentionEvents = this.hasSearchFilter(filterSet, EventFilterType.COMM_RETENTION);
    filterRecord.includeDeletedEvents = this.hasSearchFilter(filterSet, EventFilterType.DELETED);
    filterRecord.guestName = this.getSearchFilterValue(filterSet, EventFilterType.GUEST_NAME);
    filterRecord.commissionId = this.getSearchFilterValue(filterSet, EventFilterType.COMMISSION_ID);
    filterRecord.paymentReferenceId = this.getSearchFilterValue(filterSet, EventFilterType.PAYMENT_REFERENCE_ID);
    filterRecord.fundingReferenceId = this.getSearchFilterValue(filterSet, EventFilterType.FUNDING_REFERENCE_ID);
    if (isMultiVendor) {
      filterRecord.startDate = this.getSearchFilterValue(filterSet, EventFilterType.DATE_AFTER_AGENCY);
      filterRecord.endDate = this.getSearchFilterValue(filterSet, EventFilterType.DATE_BEFORE_AGENCY);
    } else {
      filterRecord.startDate = this.getSearchFilterValue(filterSet, EventFilterType.DATE_AFTER_PROPERTY);
      filterRecord.endDate = this.getSearchFilterValue(filterSet, EventFilterType.DATE_BEFORE_PROPERTY);
    }

    filterRecord.hasPrepayment =
      this.hasSearchFilterByFieldAndValue(filterSet, EventFilterType.PAYMENT_TYPE, PaymentType.PREPAYMENT);

    if (isMultiVendor) {
      if (this.hasSearchFilter(filterSet, EventFilterType.DATE_RANGE_AGENCY)) {
        const dates: string[] = EventFilterUtilsService.parseFilterValueAsDateRange(
          this.getSearchFilterValue(filterSet,
            EventFilterType.DATE_RANGE_AGENCY));
        filterRecord.startDate = dates[0];
        filterRecord.endDate = dates[1];
      }
    } else {
      if (this.hasSearchFilter(filterSet, EventFilterType.DATE_RANGE_PROPERTY)) {
        const dates: string[] = EventFilterUtilsService.parseFilterValueAsDateRange(
          this.getSearchFilterValue(filterSet,
            EventFilterType.DATE_RANGE_PROPERTY));
        filterRecord.startDate = dates[0];
        filterRecord.endDate = dates[1];
      }
    }


    let paymentStatusRecord: any = {};

    paymentStatusRecord.unpaid =
      this.hasSearchFilterByFieldAndValue(filterSet, EventFilterType.PAYMENT_STATUS, 'unpaid');

    paymentStatusRecord.inProcess =
      this.hasSearchFilterByFieldAndValue(filterSet, EventFilterType.PAYMENT_STATUS, 'paymentInProcess');

    paymentStatusRecord.paid =
      this.hasSearchFilterByFieldAndValue(filterSet, EventFilterType.PAYMENT_STATUS, 'paid');

    paymentStatusRecord.cancelled =
      this.hasSearchFilterByFieldAndValue(filterSet, EventFilterType.PAYMENT_STATUS, 'cancelled');

    const allPaymentStatusFiltersAreFalse =
      !paymentStatusRecord.unpaid &&
      !paymentStatusRecord.inProcess &&
      !paymentStatusRecord.paid &&
      !paymentStatusRecord.cancelled;

    if (allPaymentStatusFiltersAreFalse) {
      // then they they all need to be enabled
      paymentStatusRecord.unpaid = true;
      paymentStatusRecord.inProcess = true;
      paymentStatusRecord.paid = true;
      paymentStatusRecord.cancelled = true;
    }

    filterRecord.paymentStatus = paymentStatusRecord;

    let eventPaymentStatusRecord: any = {};

    eventPaymentStatusRecord.paid =
      this.hasSearchFilterByFieldAndValue(filterSet, EventFilterType.EVENT_PAYMENT_STATUS, 'paid');

    eventPaymentStatusRecord.partial =
      this.hasSearchFilterByFieldAndValue(filterSet, EventFilterType.EVENT_PAYMENT_STATUS, 'partial');

    eventPaymentStatusRecord.unpaid =
      this.hasSearchFilterByFieldAndValue(filterSet, EventFilterType.EVENT_PAYMENT_STATUS, 'unpaid');

    filterRecord.eventPaymentStatus = eventPaymentStatusRecord;

    let updatedRecord: EventMenuSearchFilter = new EventMenuSearchFilter(filterRecord);

    return updatedRecord;
  }

  private hasSearchFilter(filterSet: OrderedSet<EventSearchFilter>, filterField: EventFilterType): boolean {
    let isEmpty = filterSet.filter(filter => {
      return filterField.toString() === filter.filterField;
    }).isEmpty();
    return !isEmpty;
  }

  private getSearchFilterValue(filterSet: OrderedSet<EventSearchFilter>, filterField: EventFilterType): string {
    return filterSet.filter( (filter: EventSearchFilter) => {
      return filterField.toString() === filter.filterField;
    }).map((filter: EventSearchFilter) => filter.filterValue ).first();
  }

  private hasSearchFilterByFieldAndValue(filterSet: OrderedSet<EventSearchFilter>,
                                         filterField: EventFilterType,
                                         filterValue: string): boolean {
    let isEmpty = filterSet.filter(filter => {
      return filterField.toString() === filter.filterField && filterValue === filter.filterValue;
    }).isEmpty();
    return !isEmpty;
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/menus/event-search-filter/event-menu-search-filter-mapper.ts